/**
 * KAN-851: карточка организации клиента в приложении менеджера
 * (/m/companies/:id), только чтение: реквизиты, телефоны (звонок), почты,
 * сайт, дополнительные поля и контакты организации со ссылками на клиентов.
 */
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import { telHref } from '@/components/dashboard/attentionFormat';
import { LeadTemperatureBadge } from '@/components/leads/LeadTemperatureBadge';
import { MobileBackButton } from '@/components/mobile/MobileBackButton';
import { useMobileCompany, type MobileCompanyView } from '@/hooks/useMobileCompany';
import { mobileClientPath } from '@/lib/mobileApp';
import type { ReactNode } from 'react';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="py-2 text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className="mt-0.5 break-words text-gray-900">{children}</dd>
    </div>
  );
}

function LinkList({ values, href }: { values: string[]; href: (value: string) => string }) {
  return (
    <ul className="space-y-1">
      {values.map((value) => (
        <li key={value}>
          <a href={href(value)} className="inline-flex min-h-[32px] items-center text-blue-700">
            {value}
          </a>
        </li>
      ))}
    </ul>
  );
}

function Requisites({ view }: { view: MobileCompanyView }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4" data-testid="mobile-company-requisites">
      <h1 className="text-xl font-semibold text-gray-900">{view.name}</h1>
      <dl className="mt-2 divide-y divide-gray-100">
        {view.inn && <Row label="ИНН">{view.inn}</Row>}
        {view.ogrn && <Row label="ОГРН">{view.ogrn}</Row>}
        {view.location && <Row label="Город, регион">{view.location}</Row>}
        {view.address && <Row label="Адрес">{view.address}</Row>}
        {view.phones.length > 0 && (
          <Row label="Телефоны">
            <LinkList values={view.phones} href={telHref} />
          </Row>
        )}
        {view.emails.length > 0 && (
          <Row label="Почта">
            <LinkList values={view.emails} href={(email) => `mailto:${email}`} />
          </Row>
        )}
        {view.website && (
          <Row label="Сайт">
            <a href={view.websiteHref} target="_blank" rel="noopener noreferrer" className="text-blue-700">
              {view.website}
            </a>
          </Row>
        )}
        {view.extra.map((item) => (
          <Row key={item.name} label={item.name}>
            {item.value}
          </Row>
        ))}
      </dl>
    </section>
  );
}

function Contacts({ view }: { view: MobileCompanyView }) {
  return (
    <section data-testid="mobile-company-contacts">
      <h2 className="mb-2 px-1 text-base font-semibold text-gray-900">Контакты</h2>
      {view.contacts.length === 0 ? (
        <p className="rounded-xl border border-gray-200 bg-white p-4 text-center text-sm text-gray-500">
          Контактов нет
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
          {view.contacts.map((contact) => (
            <li key={contact.id} data-testid="mobile-company-contact">
              <Link
                to={mobileClientPath(contact.id)}
                className="flex min-h-[56px] items-center gap-2 px-4 py-3 active:bg-gray-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-base font-medium text-gray-900">
                      {contact.name || contact.phone || 'Клиент'}
                    </p>
                    <LeadTemperatureBadge level={contact.temperature} />
                  </div>
                  <p className="truncate text-sm text-gray-600">
                    {[contact.role, contact.stage?.label].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function MobileCompanyPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { view, isLoading, isError } = useMobileCompany(id);

  return (
    <div className="space-y-3 px-4 py-3" data-testid="mobile-company-page">
      <MobileBackButton />
      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-label="Загрузка" />
        </div>
      )}
      {(isError || (!isLoading && !view)) && (
        <p className="py-8 text-center text-sm text-red-600">Организация не найдена</p>
      )}
      {view && (
        <>
          <Requisites view={view} />
          <Contacts view={view} />
        </>
      )}
    </div>
  );
}
